<section class="hero" aria-label="Homepage introduction">
  <video class="hero-video" [autoplay]="true" [muted]="true" [loop]="true" playsinline>
    <source
      src="https://karimexclusives.b-cdn.net/Pavillion/mmTbMtaAg2KJWOMsvehndInUo.webm"
      type="video/webm"
    />
  </video>
  <div class="hero-shade"></div>

  <div class="hero-content">
    <p class="hero-copy">
      Design-led residences in Nairobi, built for investors, homeowners, and the city they live in.
    </p>
    <div class="hero-actions">
      <button class="primary-button" type="button" (click)="pageSelected.emit('developments')">
        View properties
      </button>
      <button class="secondary-link" type="button" (click)="homeSectionSelected.emit('process')">
        Buying Process
      </button>
    </div>
  </div>
</section>

<section
  id="developments"
  class="section developments-section"
  aria-labelledby="developments-title"
>
  <div class="section-heading-row developments-heading">
    <h2 id="developments-title">Our Developments</h2>
    <button class="text-button" type="button" (click)="inquiryRequested.emit()">
      Reserve now
    </button>
  </div>

  @if (propertiesLoading) {
    <div class="insights-state compact">
      <h3>Loading developments</h3>
      <p>Fetching properties from Django.</p>
    </div>
  } @else if (propertiesError) {
    <div class="insights-state compact">
      <h3>Developments are unavailable</h3>
      <p>Please check back shortly.</p>
    </div>
  } @else if (projects.length) {
    <div class="property-grid">
      @for (project of projects; track project.slug; let first = $first) {
        <article class="property-card" [class.property-card-wide]="first">
          <a
            class="property-media"
            href="#"
            [attr.aria-label]="'View ' + project.name + ' details'"
            (click)="selectProject($event, project.slug)"
          >
            @if (project.heroImage) {
              <img
                [src]="project.heroImage"
                [alt]="project.name + ' cover image'"
                (error)="useFallbackImage($event, project.heroFallbackImage)"
              />
            } @else {
              <div class="property-image-placeholder" aria-hidden="true"></div>
            }
            <span class="property-view">
              Details
              <svg
                class="material-icon property-view-icon"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 24 24"
              >
                <path d="M12 4 10.59 5.41 15.17 10H4v2h11.17l-4.58 4.59L12 20l8-8z" />
              </svg>
            </span>
          </a>
          <div class="property-details">
            <div>
              @if (project.logoImage) {
                <img
                  class="property-logo"
                  [src]="project.logoImage"
                  [alt]="project.name"
                  (error)="useFallbackImage($event, project.logoFallbackImage)"
                />
              }
              <h3>{{ project.name }}</h3>
            </div>
            <p class="property-price">{{ project.price }}</p>
          </div>
        </article>
      }
    </div>
  } @else {
    <div class="insights-state compact">
      <h3>No live developments yet</h3>
      <p>Live properties from Django will appear here.</p>
    </div>
  }
</section>

<section class="section buyer-steps" aria-labelledby="services-title">
  <div class="buyer-steps-bg" aria-hidden="true">
    <app-liquid-ether
      [colors]="['#053f32', '#0b6b4c', '#d8ffce']"
      [mouseForce]="16"
      [cursorSize]="140"
      [resolution]="0.48"
      [autoDemo]="true"
      [autoSpeed]="0.38"
      [autoIntensity]="1.7"
      [autoResumeDelay]="2200"
      [autoRampDuration]="0.7"
    />
  </div>
  <div class="center-heading">
    <div class="section-kicker">
      <span></span>
      Services
    </div>
    <h2 id="services-title">Booking process for buying your Pavillion property.</h2>
  </div>

  <div class="service-list">
    <article class="service-item">
      <span class="service-number">01</span>
      <div>
        <h3>Booking</h3>
        <p>
          Reserve your preferred unit with a KES 100,000 booking fee, which forms part of your
          deposit.
        </p>
      </div>
    </article>
    <article class="service-item">
      <span class="service-number">02</span>
      <div>
        <h3>Pay the deposit</h3>
        <p>
          Pay the agreed deposit to secure the unit. Standard structures can be 50/50, with a
          minimum deposit of 30%.
        </p>
      </div>
    </article>
    <article class="service-item">
      <span class="service-number">03</span>
      <div>
        <h3>Sign the sales agreement</h3>
        <p>
          Review and sign the sale agreement with the agreed payment schedule, unit details, and
          completion terms.
        </p>
      </div>
    </article>
    <article class="service-item">
      <span class="service-number">04</span>
      <div>
        <h3>Handover</h3>
        <p>
          Inspect your apartment, complete the handover checklist, and receive guidance on move-in
          preparation.
        </p>
      </div>
    </article>
    <article class="service-item">
      <span class="service-number">05</span>
      <div>
        <h3>Legal completion</h3>
        <p>
          Finalize conveyancing, registration, and ownership documents with support from the project
          and legal teams.
        </p>
      </div>
    </article>
  </div>
</section>

<section id="process" class="section process-section" aria-labelledby="process-title">
  <div class="center-heading">
    <div class="section-kicker">
      <span></span>
      Process
    </div>
    <h2 id="process-title">Starting with browsing and ending with move-in day.</h2>
  </div>

  <div class="timeline">
    <article class="timeline-item">
      <img src="/XYWwvdxgDefLRL0pgZVjb26phs_1.png" alt="Person browsing property options online" />
      <div class="timeline-marker">01</div>
      <div class="timeline-copy">
        <span>Step 01</span>
        <h3>Browsing</h3>
        <p>Shortlist homes by neighborhood, apartment type, budget, and lifestyle priorities.</p>
      </div>
    </article>

    <article class="timeline-item timeline-item-flipped">
      <img
        src="/w5ssN1VcgEVtzH0fwdQIzw3vg8_2.png"
        alt="Buyer touring a home with a property advisor"
      />
      <div class="timeline-marker">02</div>
      <div class="timeline-copy">
        <span>Step 02</span>
        <h3>Viewing</h3>
        <p>
          Visit the development, inspect sample finishes, and ask the practical questions that
          matter.
        </p>
      </div>
    </article>

    <article class="timeline-item">
      <img
        src="/8NxoJTuIiw8YEBZU0idwISohqEg_3.jpeg"
        alt="Modern apartment interior with premium finishes"
      />
      <div class="timeline-marker">03</div>
      <div class="timeline-copy">
        <span>Step 03</span>
        <h3>Reservation</h3>
        <p>
          Secure your preferred unit and receive the schedule, paperwork, and next actions in
          writing.
        </p>
      </div>
    </article>

    <article class="timeline-item timeline-item-flipped">
      <img
        src="/vcRLLLUSuqK3ajKBz2UBv2SqTo.jpg_2.jpeg"
        alt="Contemporary living room in a Pavillion residence"
      />
      <div class="timeline-marker">04</div>
      <div class="timeline-copy">
        <span>Step 04</span>
        <h3>Move-in day</h3>
        <p>
          Complete handover, receive your keys, and settle into a home prepared for everyday Nairobi
          living.
        </p>
      </div>
    </article>
  </div>
</section>

<section id="insights" class="section blog-section" aria-labelledby="blog-title">
  <div class="section-heading-row">
    <h2 id="blog-title">Blog</h2>
    <button class="text-button" type="button" (click)="pageSelected.emit('insights')">
      Read all blogs
    </button>
  </div>

  @if (blogLoading) {
    <div class="insights-state compact">
      <h3>Loading blogs</h3>
      <p>Fetching published posts from Django.</p>
    </div>
  } @else if (blogError) {
    <div class="insights-state compact">
      <h3>Blogs are unavailable</h3>
      <p>Please check back shortly.</p>
    </div>
  } @else if (blogPosts.length) {
    <div class="blog-grid">
      @for (post of blogPosts; track post.id) {
        <button class="blog-card blog-card-button" type="button" (click)="blogPostSelected.emit(post)">
          @if (post.cover_image_url) {
            <img [src]="post.cover_image_url" [alt]="post.title + ' cover image'" />
          } @else {
            <div class="blog-image-placeholder small" aria-hidden="true"></div>
          }
          <p class="blog-meta">{{ postMeta(post) }}</p>
          <h3>{{ post.title }}</h3>
        </button>
      }
    </div>
  } @else {
    <div class="insights-state compact">
      <h3>No published blogs yet</h3>
      <p>Published blog posts from Django will appear here.</p>
    </div>
  }
</section>

<section class="section faq-section" aria-labelledby="faq-title">
  <div class="faq-heading">
    <h2 id="faq-title">Frequently asked questions</h2>
    <figure class="faq-image">
      <img src="/vcRLLLUSuqK3ajKBz2UBv2SqTo.jpg_2.jpeg" alt="Pavillion residence living room" />
    </figure>
  </div>

  <div class="faq-list">
    <details open>
      <summary>Can I book a private viewing before reserving?</summary>
      <p>
        Yes. Pavillion can arrange a site visit, show-unit tour, or virtual walkthrough depending on
        the development stage.
      </p>
    </details>
    <details>
      <summary>What approvals should I ask a Kenyan developer about?</summary>
      <p>
        Ask about county approvals, project consultants, construction status, completion timelines,
        and the documents available for buyer review.
      </p>
    </details>
    <details>
      <summary>What extra costs should I budget for in Kenya?</summary>
      <p>
        Beyond the purchase price, buyers commonly plan for legal fees, stamp duty, registration
        costs, service charge setup, and moving costs.
      </p>
    </details>
    <details>
      <summary>How does the KES 100,000 booking fee work?</summary>
      <p>
        The booking fee reserves your preferred unit and is treated as part of the deposit once you
        proceed with the purchase.
      </p>
    </details>
    <details>
      <summary>Is the minimum deposit always 30%?</summary>
      <p>
        The minimum deposit starts from 30%, but the final payment structure depends on the unit,
        project stage, and agreed sale terms.
      </p>
    </details>
    <details>
      <summary>Do you support diaspora buyers?</summary>
      <p>
        Yes. The team can coordinate remote viewings, documentation, payment guidance, and regular
        development updates.
      </p>
    </details>
    <details>
      <summary>What happens after I reserve a unit?</summary>
      <p>
        You receive reservation terms, payment milestones, and the documents required to progress
        toward the sale agreement.
      </p>
    </details>
    <details>
      <summary>Can I use mortgage financing?</summary>
      <p>
        Yes, buyers can discuss mortgage financing with their bank or lender while Pavillion
        provides the property details needed for the process.
      </p>
    </details>
    <details>
      <summary>What should I know about service charge?</summary>
      <p>
        Service charge covers shared services such as security, cleaning, maintenance, and
        common-area operations. Exact amounts vary by development.
      </p>
    </details>
    <details>
      <summary>When do I receive ownership documents?</summary>
      <p>
        Ownership documentation is completed through the legal process after the required payments,
        conveyancing steps, and registration requirements are met.
      </p>
    </details>
  </div>
</section>
